/**
 * ProposalEditor — the schema-driven editor at the Feed action gate (YAG-116, §7).
 *
 * Spec: docs/superpowers/specs/2026-06-08-editable-proposal-substrate-design.md.
 *
 * Switches on `ProposalField.type` (the closed primitive set), rendering one
 * field editor each. It NEVER switches on connector or action — a new connector
 * action ships a schema annotation, not a change here. Surfaces each field's
 * suggested value / confidence / reasoning, and lets the human edit before
 * approving. Read-only fields (`editableAtGate === false`) render their value.
 */
import type { CSSProperties } from "react";
import type { EditableProposal, ProposalField } from "@yagni/shared";
import { TextField } from "./fields/TextField.js";
import { MarkdownField } from "./fields/MarkdownField.js";
import { EntityRefField } from "./fields/EntityRefField.js";
import { EnumField } from "./fields/EnumField.js";
import { MultiselectField } from "./fields/MultiselectField.js";

export interface ProposalFieldEditorProps {
  field: ProposalField;
  value: unknown;
  onChange: (value: unknown) => void;
  /**
   * DOM-id namespace for this field's control. Defaults to "proposal". The
   * staging_table Decision (YAG-239 §6.3) renders MANY proposals on one page, so
   * each row passes a per-row prefix to keep `id`/`htmlFor` pairs unique (a bare
   * "proposal-body" repeated across rows would mis-associate every label).
   */
  idPrefix?: string;
}

/** DOM id for a field control, namespaced so multiple proposals coexist. */
export function fieldDomId(idPrefix: string | undefined, key: string): string {
  return `${idPrefix ?? "proposal"}-${key}`;
}

const labelStyle: CSSProperties = {
  fontFamily: "var(--mono)",
  fontSize: 10,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "var(--ink-faded)",
  marginBottom: 4,
  display: "block",
};

const hintStyle: CSSProperties = {
  fontFamily: "var(--body)",
  fontSize: 12,
  color: "var(--ink-faded)",
  marginTop: 4,
};

/** Shared control look for inputs/selects/textareas in the gate editor. */
export const controlStyle: CSSProperties = {
  width: "100%",
  fontFamily: "var(--body)",
  fontSize: 14,
  color: "var(--ink)",
  background: "var(--paper-bright)",
  border: "1px solid var(--rule-soft)",
  borderRadius: 6,
  padding: "7px 10px",
};

function FieldEditor(props: ProposalFieldEditorProps) {
  switch (props.field.type) {
    case "markdown":
      return <MarkdownField {...props} />;
    case "entityRef":
      return <EntityRefField {...props} />;
    case "enum":
      return <EnumField {...props} />;
    case "multiselect":
      return <MultiselectField {...props} />;
    // text + the v1-deferred primitives (number/date/boolean) degrade to a text
    // input until their typed editor is built (spec §3). No connector blocks.
    case "text":
    default:
      return <TextField {...props} />;
  }
}

function FieldRow({
  field,
  value,
  onChange,
  idPrefix,
}: {
  field: ProposalField;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
  idPrefix?: string;
}) {
  const editable = field.editableAtGate !== false;
  const confidencePct =
    typeof field.confidence === "number" ? Math.round(field.confidence * 100) : null;
  return (
    <div data-testid={`proposal-field-${field.key}`}>
      <label style={labelStyle} htmlFor={fieldDomId(idPrefix, field.key)}>
        {field.label}
        {field.required ? " *" : ""}
        {confidencePct !== null && (
          <span style={{ marginLeft: 8, color: "var(--accent-warm)" }}>
            {confidencePct}% sure
          </span>
        )}
      </label>
      {editable ? (
        <FieldEditor
          field={field}
          value={value}
          onChange={(v) => onChange(field.key, v)}
          idPrefix={idPrefix}
        />
      ) : (
        <div
          data-testid={`proposal-readonly-${field.key}`}
          style={{ ...controlStyle, background: "var(--paper)", color: "var(--ink-soft)" }}
        >
          {formatReadonly(value)}
        </div>
      )}
      {field.reasoning && <p style={hintStyle}>{field.reasoning}</p>}
    </div>
  );
}

function formatReadonly(value: unknown): string {
  if (value == null) return "—";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

export function ProposalEditor({
  proposal,
  values,
  onChange,
  idPrefix,
}: {
  proposal: EditableProposal;
  /** Edited overrides keyed by field key; falls back to each field's value. */
  values?: Record<string, unknown> | null;
  onChange: (key: string, value: unknown) => void;
  /** DOM-id namespace (default "proposal"); pass a per-row prefix when many
   * proposals render on one page (the staging_table Decision, §6.3). */
  idPrefix?: string;
}) {
  return (
    <div className="space-y-3" data-testid="proposal-editor">
      {proposal.fields.map((field) => (
        <FieldRow
          key={field.key}
          field={field}
          value={values && field.key in values ? values[field.key] : field.value}
          onChange={onChange}
          idPrefix={idPrefix}
        />
      ))}
    </div>
  );
}
